import { action } from 'typesafe-actions';

import {
  CreateIPCameraDto,
  FetchAllowedPlateNumbersParams,
  FetchIPCameraPhotosParams,
  IPCameraTriggerType
} from 'types/dto/ip-camera.dto';
import { EnterpriseId } from 'types/models/enterprise';
import IPCamera, {
  IPCameraCapabilities,
  IPCameraChannel,
  IPCameraEditableFields,
  IPCameraEvent,
  IPCameraMetadata,
  IPCameraPTZPresetEditableFields,
  IPCameraPhoto,
  IPCameraPreview,
  IPCameraSettings,
  PTZControl,
  PTZPreset
} from 'types/models/ip-camera';
import { SiteId } from 'types/models/site';

import ActionTypes from './constants';
import { IPCameraPhotosState, IPCamerasState, PlateNumber, SetAllowedPlateNumbersPayload } from './types';

// ================================================
// IP CAMERA LIST
// ================================================
export const fetchIPCameras = (enterpriseId: EnterpriseId, siteId?: SiteId) =>
  action(ActionTypes.FETCH_IP_CAMERAS_REQUEST, { enterpriseId, siteId });

export const fetchIPCamerasSuccess = (ipCameras: IPCamerasState) =>
  action(ActionTypes.FETCH_IP_CAMERAS_SUCCESS, ipCameras);

export const fetchIPCamerasFailure = (message: string) =>
  action(ActionTypes.FETCH_IP_CAMERAS_FAILURE, { message });

// ================================================
// IP CAMERA DETAILS
// ================================================
export const fetchCameraById = (id: number) =>
  action(ActionTypes.FETCH_CAMERA_BY_ID_REQUEST, { id });

export const fetchCameraByIdSuccess = (ipCamera: IPCamera) =>
  action(ActionTypes.FETCH_CAMERA_BY_ID_SUCCESS, ipCamera);

export const fetchCameraByIdFailure = (message: string) =>
  action(ActionTypes.FETCH_CAMERA_BY_ID_FAILURE, { message }, undefined, true);

// ================================================
// IP CAMERA PREVIEW
// ================================================
export const takeIPCameraPreview = (ipCameraId: number) =>
  action(ActionTypes.TAKE_IP_CAMERA_PREVIEW_REQUEST, { ipCameraId });

export const takeIPCameraPreviewSuccess = () => action(ActionTypes.TAKE_IP_CAMERA_PREVIEW_SUCCESS);

export const takeIPCameraPreviewFailure = (message: string) =>
  action(ActionTypes.TAKE_IP_CAMERA_PREVIEW_FAILURE, { message }, undefined, true);

export const setIPCameraPreview = (ipCameraId: number, preview: IPCameraPreview) =>
  action(ActionTypes.SET_IP_CAMERA_PREVIEW, { ipCameraId, preview });

export const setIPCameraPreviewError = (ipCameraId: number, error: string) =>
  action(ActionTypes.SET_IP_CAMERA_PREVIEW_ERROR, { ipCameraId, error });

// ================================================
// IP CAMERA STATE (e.g. online status)
// ================================================
export const fetchIPCameraState = (ipCameraId: number) =>
  action(ActionTypes.FETCH_IP_CAMERA_STATE_REQUEST, { ipCameraId });

export const fetchIPCameraStateSuccess = () => action(ActionTypes.FETCH_IP_CAMERA_STATE_SUCCESS);

export const fetchIPCameraStateFailure = (message: string) =>
  action(ActionTypes.FETCH_IP_CAMERA_STATE_FAILURE, { message }, undefined, true);

export const setIPCameraState = (ipCameraId: number, metadata: IPCameraMetadata) =>
  action(ActionTypes.SET_IP_CAMERA_STATE, { ipCameraId, metadata });

export const snoozeIPCamera = (ipCameraId: number, snoozeHours: number) =>
  action(ActionTypes.SNOOZE_IP_CAMERA_REQUEST, { ipCameraId, snoozeHours });

export const snoozeIPCameraSuccess = () =>
  action(ActionTypes.SNOOZE_IP_CAMERA_SUCCESS);

export const snoozeIPCameraFailure = (message: string) =>
  action(ActionTypes.SNOOZE_IP_CAMERA_FAILURE, { message }, undefined, true);

export const wakeUpIPCamera = (ipCameraId: number) =>
  action(ActionTypes.WAKE_UP_IP_CAMERA_REQUEST, { ipCameraId });

export const wakeUpIPCameraSuccess = () =>
  action(ActionTypes.WAKE_UP_IP_CAMERA_SUCCESS);

export const wakeUpIPCameraFailure = (message: string) =>
  action(ActionTypes.WAKE_UP_IP_CAMERA_FAILURE, { message }, undefined, true);

export const fetchIPCameraCapabilities = (
  ipCameraId: number,
  capabilityFields?: (keyof IPCameraCapabilities)[]
) =>
  action(ActionTypes.FETCH_IP_CAMERA_CAPABILITIES_REQUEST, { ipCameraId, capabilityFields });

export const fetchIPCameraCapabilitiesSuccess = () => action(ActionTypes.FETCH_IP_CAMERA_CAPABILITIES_SUCCESS);

export const fetchIPCameraCapabilitiesFailure = (message: string) =>
  action(ActionTypes.FETCH_IP_CAMERA_CAPABILITIES_FAILURE, { message }, undefined, true);

export const setIPCameraCapabilities = (
  ipCameraId: number,
  partialCapabilities: Partial<IPCameraCapabilities>,
  channels?: IPCameraChannel[]
) =>
  action(ActionTypes.SET_IP_CAMERA_CAPABILITIES, { ipCameraId, partialCapabilities, channels });

// ================================================
// IP CAMERA PHOTOS
// ================================================
export const fetchIPCameraPhotos = (ipCameraId: number, params: FetchIPCameraPhotosParams) =>
  action(ActionTypes.FETCH_IP_CAMERA_PHOTOS_REQUEST, { ipCameraId, params });

export const fetchIPCameraPhotosSuccess = () => action(ActionTypes.FETCH_IP_CAMERA_PHOTOS_SUCCESS);

export const fetchIPCameraPhotosFailure = (message: string) =>
  action(ActionTypes.FETCH_IP_CAMERA_PHOTOS_FAILURE, { message }, undefined, true);

export const setIPCameraPhotos = (ipCameraId: number, photoList: IPCameraPhotosState) =>
  action(ActionTypes.SET_IP_CAMERA_PHOTOS, { ipCameraId, photoList });

export const takeIPCameraPhoto = (ipCameraId: number, channelId: number) =>
  action(ActionTypes.TAKE_IP_CAMERA_PHOTO_REQUEST, { ipCameraId, channelId });

export const takeIPCameraPhotoSuccess = () => action(ActionTypes.TAKE_IP_CAMERA_PHOTO_SUCCESS);

export const takeIPCameraPhotoFailure = (message: string) =>
  action(ActionTypes.TAKE_IP_CAMERA_PHOTO_FAILURE, { message }, undefined, true);

export const setIPCameraUnseenPhoto = (ipCameraId: number, newPhoto: IPCameraPhoto) =>
  action(ActionTypes.SET_IP_CAMERA_UNSEEN_PHOTO, { ipCameraId, newPhoto });

export const resetIPCameraUnseenPhoto = (ipCameraId: number) =>
  action(ActionTypes.SET_IP_CAMERA_UNSEEN_PHOTO, { ipCameraId, newPhoto: undefined });

// ================================================
// IP CAMERA TRIGGER
// ================================================
export const triggerIPCamera = (ipCameraId: number, triggerType: IPCameraTriggerType) =>
  action(ActionTypes.TRIGGER_IP_CAMERA_REQUEST, { ipCameraId, triggerType });

export const triggerIPCameraSuccess = () => action(ActionTypes.TRIGGER_IP_CAMERA_SUCCESS);

export const triggerIPCameraFailure = (message: string) =>
  action(ActionTypes.TRIGGER_IP_CAMERA_FAILURE, { message }, undefined, true);

// ================================================
// IP CAMERA SETTINGS
// ================================================
export const fetchIPCameraSettings = (ipCameraId: number) =>
  action(ActionTypes.FETCH_IP_CAMERA_SETTINGS_REQUEST, { ipCameraId });

export const fetchIPCameraSettingsSuccess = () =>
  action(ActionTypes.FETCH_IP_CAMERA_SETTINGS_SUCCESS);

export const fetchIPCameraSettingsFailure = (ipCameraId: number, message: string) =>
  action(ActionTypes.FETCH_IP_CAMERA_SETTINGS_FAILURE, { ipCameraId, message }, undefined, true);

export const setIPCameraSettings = (ipCameraId: number, settings: IPCameraSettings) =>
  action(ActionTypes.SET_IP_CAMERA_SETTINGS, { ipCameraId, settings });

export const editIPCameraSettings = (ipCameraId: number, values: Partial<IPCameraSettings>) =>
  action(ActionTypes.EDIT_IP_CAMERA_SETTINGS_REQUEST, { ipCameraId, values });

export const editIPCameraSettingsSuccess = () =>
  action(ActionTypes.EDIT_IP_CAMERA_SETTINGS_SUCCESS);

export const editIPCameraSettingsFailure = (message: string) =>
  action(ActionTypes.EDIT_IP_CAMERA_SETTINGS_FAILURE, { message }, undefined, true);

// ================================================
// IP CAMERA PTZ
// ================================================
export const sendCameraPTZControlRequest = (id: number, control: PTZControl) =>
  action(ActionTypes.SEND_CAMERA_PTZ_CONTROL_REQUEST, { id, control });

// ================================================
// IP CAMERA EVENTS
// ================================================
export const fetchIPCameraEvents = (
  ipCameraId: number,
  startDate: string,
  endDate: string,
  isAppend: boolean,
  channelId: number,
  /**
   * When false, Load more merges overlapping events at the page boundary
   * (see mergeOverlappedEventArray). When true (play-by-filename), append
   * as-is — each row keeps its own filePath.
   */
  disableIpCameraEventOverlapMerge: boolean = false,
  paging?: { offset: number; limit: number }
) =>
  action(ActionTypes.FETCH_IP_CAMERA_EVENTS_REQUEST, {
    ipCameraId,
    startDate,
    endDate,
    isAppend,
    channelId,
    disableIpCameraEventOverlapMerge,
    paging
  });

export const fetchIPCameraEventsSuccess = () => action(ActionTypes.FETCH_IP_CAMERA_EVENTS_SUCCESS);

export const fetchIPCameraEventsFailure = (message: string) =>
  action(ActionTypes.FETCH_IP_CAMERA_EVENTS_FAILURE, { message }, undefined, true);

export const setIPCameraEvents = (ipCameraId: number, events: IPCameraEvent[]) =>
  action(ActionTypes.SET_IP_CAMERA_EVENTS, { ipCameraId, events });

export const resetIPCameraEvents = (ipCameraId: number) =>
  action(ActionTypes.SET_IP_CAMERA_EVENTS, { ipCameraId, events: undefined });

// ================================================
// IP CAMERA EDIT
// ================================================
export const editIPCamera = (id: number, values: IPCameraEditableFields) =>
  action(ActionTypes.EDIT_IP_CAMERA_REQUEST, { id, values });

export const editIPCameraSuccess = () => action(ActionTypes.EDIT_IP_CAMERA_SUCCESS);

export const editIPCameraFailure = (message: string) =>
  action(ActionTypes.EDIT_IP_CAMERA_FAILURE, { message }, undefined, true);

// ================================================
// IP CAMERA REMOVAL
// ================================================
export const removeIPCamera = (id: number, backToIPCameraListPage: () => void) =>
  action(ActionTypes.REMOVE_IP_CAMERA_REQUEST, { id, backToIPCameraListPage });

export const removeIPCameraSuccess = () => action(ActionTypes.REMOVE_IP_CAMERA_SUCCESS);

export const removeIPCameraFailure = (message: string) =>
  action(ActionTypes.REMOVE_IP_CAMERA_FAILURE, { message }, undefined, true);

export const removeFromIPCameraList = (id: number) =>
  action(ActionTypes.REMOVE_FROM_IP_CAMERA_LIST, { id });

// ================================================
// IP CAMERA PTZ PRESETS
// ================================================
export const fetchIPCameraPTZPresetsRequest = (ipCameraId: number, channelId: number) =>
  action(ActionTypes.FETCH_IP_CAMERA_PTZ_PRESETS_REQUEST, { ipCameraId, channelId });

export const fetchIPCameraPTZPresetsSuccess = () =>
  action(ActionTypes.FETCH_IP_CAMERA_PTZ_PRESETS_SUCCESS);

export const fetchIPCameraPTZPresetsFailure = (message: string) =>
  action(ActionTypes.FETCH_IP_CAMERA_PTZ_PRESETS_FAILURE, { message }, undefined, true);

export const createIPCameraPTZPresetRequest = (ipCameraId: number, channelId: number) =>
  action(ActionTypes.CREATE_IP_CAMERA_PTZ_PRESET_REQUEST, { ipCameraId, channelId });

export const createIPCameraPTZPresetSuccess = () =>
  action(ActionTypes.CREATE_IP_CAMERA_PTZ_PRESET_SUCCESS);

export const createIPCameraPTZPresetFailure = (message: string) =>
  action(ActionTypes.CREATE_IP_CAMERA_PTZ_PRESET_FAILURE, { message }, undefined, true);

export const activateIPCameraPTZPresetRequest = (ipCameraId: number, presetId: number, channelId: number) =>
  action(ActionTypes.ACTIVATE_IP_CAMERA_PTZ_PRESET_REQUEST, { ipCameraId, presetId, channelId });

export const activateIPCameraPTZPresetSuccess = () =>
  action(ActionTypes.ACTIVATE_IP_CAMERA_PTZ_PRESET_SUCCESS);

export const activateIPCameraPTZPresetFailure = (message: string) =>
  action(ActionTypes.ACTIVATE_IP_CAMERA_PTZ_PRESET_FAILURE, { message }, undefined, true);

export const updateIPCameraPTZPresetRequest = (
  ipCameraId: number,
  presetId: number,
  values: IPCameraPTZPresetEditableFields,
  channelId: number
) =>
  action(ActionTypes.UPDATE_IP_CAMERA_PTZ_PRESET_REQUEST, { ipCameraId, presetId, values, channelId });

export const updateIPCameraPTZPresetSuccess = () =>
  action(ActionTypes.UPDATE_IP_CAMERA_PTZ_PRESET_SUCCESS);

export const updateIPCameraPTZPresetFailure = (message: string) =>
  action(ActionTypes.UPDATE_IP_CAMERA_PTZ_PRESET_FAILURE, { message }, undefined, true);

export const deleteIPCameraPTZPresetRequest = (ipCameraId: number, presetId: number, channelId: number) =>
  action(ActionTypes.DELETE_IP_CAMERA_PTZ_PRESET_REQUEST, { ipCameraId, presetId, channelId });

export const deleteIPCameraPTZPresetSuccess = () =>
  action(ActionTypes.DELETE_IP_CAMERA_PTZ_PRESET_SUCCESS);

export const deleteIPCameraPTZPresetFailure = (message: string) =>
  action(ActionTypes.DELETE_IP_CAMERA_PTZ_PRESET_FAILURE, { message }, undefined, true);

export const setIPCameraPTZPresets = (ipCameraId, presets: PTZPreset[]) =>
  action(ActionTypes.SET_IP_CAMERA_PTZ_PRESETS, { ipCameraId, presets });

export const setIPCameraPTZPreset = (ipCameraId, preset: PTZPreset) =>
  action(ActionTypes.SET_IP_CAMERA_PTZ_PRESET, { ipCameraId, preset });

export const removeIPCameraPTZPreset = (ipCameraId: number, presetId: number) =>
  action(ActionTypes.REMOVE_IP_CAMERA_PTZ_PRESET, { ipCameraId, presetId });

// ================================================
// IP CAMERA CREATION
// ================================================
export const createIPCamera = (newIPCamera: CreateIPCameraDto, onSuccess?: () => void) =>
  action(ActionTypes.CREATE_IP_CAMERA_REQUEST, { newIPCamera, onSuccess });

export const createIPCameraSuccess = () =>
  action(ActionTypes.CREATE_IP_CAMERA_SUCCESS);

export const createIPCameraFailure = (message: string) =>
  action(ActionTypes.CREATE_IP_CAMERA_FAILURE, { message }, undefined, true);

// ================================================
// IP CAMERA ALLOWED PLATE NUMBERS
// ================================================
export const fetchAllowedPlateNumbers = (ipCameraId: number, params: FetchAllowedPlateNumbersParams) =>
  action(ActionTypes.FETCH_ALLOWED_PLATE_NUMBERS_REQUEST, { ipCameraId, params, isAppend: false });

export const fetchMoreAllowedPlateNumbers = (ipCameraId: number, params: FetchAllowedPlateNumbersParams) =>
  action(ActionTypes.FETCH_ALLOWED_PLATE_NUMBERS_REQUEST, { ipCameraId, params, isAppend: true });

export const fetchAllowedPlateNumbersSuccess = () =>
  action(ActionTypes.FETCH_ALLOWED_PLATE_NUMBERS_SUCCESS);

export const fetchAllowedPlateNumbersFailure = (message: string) =>
  action(ActionTypes.FETCH_ALLOWED_PLATE_NUMBERS_FAILURE, { message }, undefined, true);

export const setAllowedPlateNumbers = (payload: SetAllowedPlateNumbersPayload) =>
  action(ActionTypes.SET_ALLOWED_PLATE_NUMBERS, payload);

export const setAllowedPlateNumbersSearch = (ipCameraId: number, search: string) =>
  action(ActionTypes.SET_ALLOWED_PLATE_NUMBERS_SEARCH, { ipCameraId, search });

export const prependAllowedPlateNumbers = (ipCameraId: number, plateNumbers: PlateNumber[], addedTotal: number) =>
  action(ActionTypes.PREPEND_ALLOWED_PLATE_NUMBERS, { ipCameraId, plateNumbers, addedTotal });

export const removeAllowedPlateNumbersFromList = (ipCameraId: number, plateNumbers: PlateNumber[]) =>
  action(ActionTypes.REMOVE_ALLOWED_PLATE_NUMBERS_FROM_LIST, { ipCameraId, plateNumbers });

export const addAllowedPlateNumber = (ipCameraId: number, plateNumber: PlateNumber) =>
  action(ActionTypes.ADD_ALLOWED_PLATE_NUMBER_REQUEST, { ipCameraId, plateNumber });

export const addAllowedPlateNumberSuccess = () =>
  action(ActionTypes.ADD_ALLOWED_PLATE_NUMBER_SUCCESS);

export const addAllowedPlateNumberFailure = (message: string) =>
  action(ActionTypes.ADD_ALLOWED_PLATE_NUMBER_FAILURE, { message }, undefined, true);

export const deleteAllowedPlateNumber = (
  ipCameraId: number,
  plateNumber: PlateNumber,
  onSuccess?: () => void
) => action(ActionTypes.DELETE_ALLOWED_PLATE_NUMBER_REQUEST, { ipCameraId, plateNumber, onSuccess });

export const deleteAllowedPlateNumberSuccess = () =>
  action(ActionTypes.DELETE_ALLOWED_PLATE_NUMBER_SUCCESS);

export const deleteAllowedPlateNumberFailure = (message: string) =>
  action(ActionTypes.DELETE_ALLOWED_PLATE_NUMBER_FAILURE, { message }, undefined, true);
